/**
 * `InstallCoverageChips` — ADR-0212 Rule 3. One strip above the active
 * install panel: THIS INSTALL COVERS + up to four chips in the same
 * visual grammar as the coverage card's rows (16px check / dash box +
 * label), so a merchant learns the four layers once at install and
 * recognises them verbatim after Verify and in Settings.
 *
 * The tabs PROMISE (what this install gives you); the card MEASURES.
 * A muted chip carries a two-or-three-word reason ("needs Cloudflare
 * or a plugin", "not possible on Shopify", "Step 2") so a not-covered
 * layer never needs a sentence of prose.
 */

import { Check } from 'lucide-react';

import type { DetectPlatformResult } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { Tooltip } from '../../components/atoms/index.js';
import { LAYER_LABEL, type LayerKey } from './install-copy.js';
import type { InstallMethod } from './InstallMethodTabs.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type ChipState = 'covered' | 'not_covered';

export interface CoverageChip {
  readonly key: LayerKey;
  readonly label: string;
  readonly state: ChipState;
  /** Shown after a dash on a not-covered chip. */
  readonly reason?: string;
}

/** Pure — what each tab covers. The store chip appears only where a store is in play. */
export function coverageChipsFor(
  tab: InstallMethod,
  detected: DetectPlatformResult | null,
): readonly CoverageChip[] {
  const c = (key: LayerKey): CoverageChip => ({ key, label: LAYER_LABEL[key], state: 'covered' });
  const n = (key: LayerKey, reason: string): CoverageChip => ({ key, label: LAYER_LABEL[key], state: 'not_covered', reason });
  const store_platform = detected?.platform === 'woocommerce' || detected?.platform === 'shopify';
  switch (tab) {
    case 'script_tag':
    case 'custom_embed':
      return [c('browser'), n('server_side', 'needs Cloudflare or a plugin'), c('discovery')];
    case 'wordpress':
      return [c('browser'), c('server_side'), c('discovery')];
    case 'woocommerce':
      return [c('browser'), c('server_side'), c('discovery'), n('store', 'Step 2')];
    case 'shopify':
      return [c('browser'), n('server_side', 'not possible on Shopify'), c('discovery'), c('store')];
    case 'cloudflare':
      return store_platform
        ? [c('browser'), c('server_side'), c('discovery'), n('store', 'connect it after')]
        : [c('browser'), c('server_side'), c('discovery')];
  }
}

export function InstallCoverageChips({
  tab,
  detected,
}: {
  readonly tab: InstallMethod;
  readonly detected: DetectPlatformResult | null;
}): JSX.Element {
  const C = useC();
  const chips = coverageChipsFor(tab, detected);
  return (
    <div className="mb-4" data-testid="install-coverage-chips">
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, marginBottom: 6 }}>
        THIS INSTALL COVERS
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {chips.map((chip) => {
          const on = chip.state === 'covered';
          // Not-covered chips carry the reason in a Tooltip (2026-10-21) —
          // the row-level dash now reads clean, and the explanation is one
          // hover/tap away. Covered chips need no tooltip.
          const inner = (
            <div
              key={chip.key}
              className="flex items-center gap-2"
              data-testid={`coverage-chip-${chip.key}`}
              data-state={chip.state}
            >
              <span
                aria-hidden
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 14, height: 14, borderRadius: 2,
                  background: on ? C.limeDim : 'transparent',
                  border: `1px solid ${on ? C.limeFg : C.border}`,
                  color: on ? C.limeFg : C.dim,
                  fontFamily: monoFont, fontSize: 9, lineHeight: 1,
                }}
              >
                {on ? <Check size={10} strokeWidth={3} /> : '—'}
              </span>
              <span style={{ fontFamily: sansFont, fontSize: 12, color: on ? C.text : C.muted }}>
                {chip.label}
              </span>
            </div>
          );
          if (!on && chip.reason !== undefined) {
            return (
              <Tooltip key={chip.key} content={chip.reason}>
                {inner}
              </Tooltip>
            );
          }
          return <span key={chip.key}>{inner}</span>;
        })}
      </div>
    </div>
  );
}
